import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type SetStateAction,
} from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { useQuery } from "@tanstack/react-query"

import {
  getTransportCollectionItems,
  getTransportCollectionNextRequest,
  type TransportOperationName,
  type TransportOperationResponse,
  type TransportOperationVersionNumber,
} from "@/types_api/backend/transport/operations"

import { TransportRequestError } from "../utils/transportClient"
import { reportTransportError } from "../utils/transportErrorReporting"
import {
  executeTransportOperation,
  resolveTransportOperationName,
} from "./transportOperationRuntime"
import { getTransportPaginatedQueryKey } from "./transportQueryKeys"
import type {
  TransportCollectionData,
  TransportCollectionHookParams,
  TransportCollectionItem,
  TransportCollectionOperationName,
  TransportPaginatedHookParams,
  TransportPaginatedQueryKey,
  TransportPaginatedQueryResult,
  TransportPaginationState,
  TransportResolvedRequest,
  TransportRuntimeHookParams,
  TransportRuntimePaginatedHookParams,
  TransportVersionedCollectionHookParams,
  TransportVersionedCollectionHookParamsForName,
  TransportVersionedCollectionOperationName,
  TransportVersionedOperationGroup,
  TransportVersionedPaginatedHookParams,
} from "./transportQueryTypes"
import { useTransportOperationContext } from "./useTransportOperationContext"

export function useTransportPaginatedQuery<
  Name extends TransportOperationName,
  TData,
>(
  params: TransportPaginatedHookParams<Name, TData>,
): TransportPaginatedQueryResult<TData>

export function useTransportPaginatedQuery<
  Group extends TransportVersionedOperationGroup,
  Version extends TransportOperationVersionNumber<Group>,
  TData,
>(
  params: TransportVersionedPaginatedHookParams<Group, Version, TData>,
): TransportPaginatedQueryResult<TData>

export function useTransportPaginatedQuery<
  Name extends TransportOperationName,
  TData,
>(params: unknown): TransportPaginatedQueryResult<TData> {
  const runtimeParams = params as TransportRuntimePaginatedHookParams<
    Name,
    TData
  >
  const {
    combinePages,
    getPageItemCount,
    getNextRequest,
    getTotalItemCount,
    maxPages = 100,
    onMaxPages = "error",
    options,
    queryKeyScope,
  } = runtimeParams
  const operation = resolveTransportOperationName(runtimeParams)
  const {
    reportErrors = true,
    abortOnUnmount = false,
    ...queryOptions
  } = options ?? {}
  const request = "request" in runtimeParams ? runtimeParams.request : undefined
  const requestReady = !("request" in runtimeParams) || request != null
  const typedOperation = operation as Name
  const typedRequest = request
  const {
    getClient,
    isReady,
    organizationSlug,
    queryOrganizationSlug,
    queryWorkspaceId,
  } = useTransportOperationContext(operation)

  const queryKey = useMemo(
    () =>
      getTransportPaginatedQueryKey(
        typedOperation,
        typedRequest,
        queryOrganizationSlug,
        queryWorkspaceId,
        queryKeyScope,
      ),
    [
      queryKeyScope,
      queryOrganizationSlug,
      queryWorkspaceId,
      typedOperation,
      typedRequest,
    ],
  )

  const enabled = (options?.enabled ?? true) && isReady && requestReady
  const isMountedRef = useRef(true)
  const latestPaginationRunRef = useRef<symbol | null>(null)
  const [pagination, setPagination] = useState<TransportPaginationState>({
    hasNextPage: false,
    isFetching: false,
    itemsLoaded: 0,
    maxPages,
    pagesLoaded: 0,
    totalItems: null,
  })

  const setPaginationIfMounted = useCallback(
    (next: SetStateAction<TransportPaginationState>) => {
      if (isMountedRef.current) {
        setPagination(next)
      }
    },
    [],
  )

  const setPaginationForRun = useCallback(
    (runId: symbol, next: SetStateAction<TransportPaginationState>) => {
      if (isMountedRef.current && latestPaginationRunRef.current === runId) {
        setPagination(next)
      }
    },
    [],
  )

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      latestPaginationRunRef.current = null
      setPaginationIfMounted({
        hasNextPage: false,
        isFetching: false,
        itemsLoaded: 0,
        maxPages,
        pagesLoaded: 0,
        totalItems: null,
      })
    }
  }, [enabled, maxPages, setPaginationIfMounted])

  const query = useQuery<
    TData,
    TransportRequestError,
    TData,
    TransportPaginatedQueryKey<Name>
  >({
    ...CACHE_PROFILES.standard,
    ...queryOptions,
    enabled,
    queryKey,
    queryFn: async (context) => {
      if (!requestReady) {
        return null as TData
      }

      const signal = abortOnUnmount ? context.signal : undefined
      const paginationRunId = Symbol(operation)
      const pages: TransportOperationResponse<Name>[] = []
      let currentRequest = typedRequest ?? undefined
      let itemsLoaded = 0
      let totalItems: null | number = null

      latestPaginationRunRef.current = paginationRunId
      setPaginationForRun(paginationRunId, {
        hasNextPage: false,
        isFetching: true,
        itemsLoaded: 0,
        maxPages,
        pagesLoaded: 0,
        totalItems: null,
      })

      try {
        for (let pageNumber = 0; pageNumber < maxPages; pageNumber += 1) {
          signal?.throwIfAborted()
          const page = await executeTransportOperation(
            getClient(),
            typedOperation,
            currentRequest,
            signal ? { signal } : undefined,
          )
          signal?.throwIfAborted()
          pages.push(page)

          itemsLoaded += getPageItemCount?.(page) ?? 0
          totalItems = getTotalItemCount?.(page) ?? totalItems

          const nextRequest = getNextRequest(page, currentRequest)
          const returnPartial =
            Boolean(nextRequest) &&
            pageNumber === maxPages - 1 &&
            onMaxPages === "return-partial"
          setPaginationForRun(paginationRunId, {
            hasNextPage: Boolean(nextRequest),
            isFetching: Boolean(nextRequest) && !returnPartial,
            itemsLoaded,
            maxPages,
            pagesLoaded: pages.length,
            totalItems,
          })

          if (!nextRequest || returnPartial) {
            return combinePages(pages)
          }

          currentRequest = nextRequest
        }

        throw new TransportRequestError(
          `Transport paginated query "${operation}" exceeded ${maxPages} pages.`,
          0,
        )
      } catch (error) {
        setPaginationForRun(paginationRunId, (current) => ({
          ...current,
          hasNextPage: false,
          isFetching: false,
          maxPages,
        }))
        throw error
      }
    },
  })

  useEffect(() => {
    if (reportErrors && query.error) {
      reportTransportError({
        error: query.error,
        kind: "paginated_query",
        operation,
        organizationSlug,
        errorUpdatedAt: query.errorUpdatedAt,
      })
    }
  }, [
    operation,
    organizationSlug,
    query.error,
    query.errorUpdatedAt,
    reportErrors,
  ])

  return {
    ...query,
    pagination,
  }
}

export function useTransportCollectionQuery<
  Name extends TransportCollectionOperationName,
  TItem = TransportCollectionItem<Name>,
>(
  params: TransportCollectionHookParams<Name, TItem>,
): TransportPaginatedQueryResult<TransportCollectionData<Name, TItem>>

export function useTransportCollectionQuery<
  Group extends TransportVersionedOperationGroup,
  Version extends TransportOperationVersionNumber<Group>,
  Name extends TransportVersionedCollectionOperationName<Group, Version>,
  TItem = TransportCollectionItem<Name>,
>(
  params: TransportVersionedCollectionHookParams<Group, Version, TItem>,
): TransportPaginatedQueryResult<TransportCollectionData<Name, TItem>>

export function useTransportCollectionQuery<
  Name extends TransportCollectionOperationName,
  TItem = TransportCollectionItem<Name>,
>(
  params: unknown,
): TransportPaginatedQueryResult<TransportCollectionData<Name, TItem>> {
  const runtimeParams = params as
    | TransportCollectionHookParams<Name, TItem>
    | TransportVersionedCollectionHookParamsForName<Name, TItem>
  const { maxPages, options } = runtimeParams
  const operation = resolveTransportOperationName(
    runtimeParams as TransportRuntimeHookParams<
      TransportOperationName,
      unknown
    >,
  ) as Name

  return useTransportPaginatedQuery({
    ...runtimeParams,
    combinePages: (pages: TransportOperationResponse<Name>[]) => ({
      items: pages.flatMap((page: TransportOperationResponse<Name>) =>
        getTransportCollectionItems<Name, TItem>(operation, page),
      ),
      pages,
    }),
    getNextRequest: (
      lastPage: TransportOperationResponse<Name>,
      previousRequest: TransportResolvedRequest<Name> | undefined,
    ) =>
      getTransportCollectionNextRequest(operation, lastPage, previousRequest),
    getPageItemCount: (page: TransportOperationResponse<Name>) =>
      getTransportCollectionItems<Name, TItem>(operation, page).length,
    maxPages,
    options,
  } as TransportPaginatedHookParams<Name, TransportCollectionData<Name, TItem>>)
}
